/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `user_profile` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class UserProfileMesg : Mesg {
    public constructor() : super("UserProfile", Profile.MesgNum.USER_PROFILE)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** message_index. */
    public var messageIndex: UShort?
        get() = getFieldValue(MESSAGE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MESSAGE_INDEX_FIELD_NUM, value)

    /** friendly_name. Used for Morning Report greeting */
    public var friendlyName: String?
        get() = getFieldValue(FRIENDLY_NAME_FIELD_NUM) as? String
        set(value) = setFieldValue(FRIENDLY_NAME_FIELD_NUM, value)

    /** gender. */
    public var gender: Gender?
        get() = (getFieldValue(GENDER_FIELD_NUM) as? UByte)?.let { Gender.fromValue(it) }
        set(value) = setFieldValue(GENDER_FIELD_NUM, value?.value)

    /** age, in years. */
    public var age: UByte?
        get() = getFieldValue(AGE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(AGE_FIELD_NUM, value)

    /** height, in m. */
    public var height: Double?
        get() = getFieldValue(HEIGHT_FIELD_NUM) as? Double
        set(value) = setFieldValue(HEIGHT_FIELD_NUM, value)

    /** weight, in kg. */
    public var weight: Double?
        get() = getFieldValue(WEIGHT_FIELD_NUM) as? Double
        set(value) = setFieldValue(WEIGHT_FIELD_NUM, value)

    /** language. */
    public var language: Language?
        get() = (getFieldValue(LANGUAGE_FIELD_NUM) as? UByte)?.let { Language.fromValue(it) }
        set(value) = setFieldValue(LANGUAGE_FIELD_NUM, value?.value)

    /** elev_setting. */
    public var elevSetting: DisplayMeasure?
        get() = (getFieldValue(ELEV_SETTING_FIELD_NUM) as? UByte)?.let { DisplayMeasure.fromValue(it) }
        set(value) = setFieldValue(ELEV_SETTING_FIELD_NUM, value?.value)

    /** weight_setting. */
    public var weightSetting: DisplayMeasure?
        get() = (getFieldValue(WEIGHT_SETTING_FIELD_NUM) as? UByte)?.let { DisplayMeasure.fromValue(it) }
        set(value) = setFieldValue(WEIGHT_SETTING_FIELD_NUM, value?.value)

    /** resting_heart_rate, in bpm. */
    public var restingHeartRate: UByte?
        get() = getFieldValue(RESTING_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(RESTING_HEART_RATE_FIELD_NUM, value)

    /** default_max_running_heart_rate, in bpm. */
    public var defaultMaxRunningHeartRate: UByte?
        get() = getFieldValue(DEFAULT_MAX_RUNNING_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(DEFAULT_MAX_RUNNING_HEART_RATE_FIELD_NUM, value)

    /** default_max_biking_heart_rate, in bpm. */
    public var defaultMaxBikingHeartRate: UByte?
        get() = getFieldValue(DEFAULT_MAX_BIKING_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(DEFAULT_MAX_BIKING_HEART_RATE_FIELD_NUM, value)

    /** default_max_heart_rate, in bpm. */
    public var defaultMaxHeartRate: UByte?
        get() = getFieldValue(DEFAULT_MAX_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(DEFAULT_MAX_HEART_RATE_FIELD_NUM, value)

    /** hr_setting. */
    public var hrSetting: DisplayHeart?
        get() = (getFieldValue(HR_SETTING_FIELD_NUM) as? UByte)?.let { DisplayHeart.fromValue(it) }
        set(value) = setFieldValue(HR_SETTING_FIELD_NUM, value?.value)

    /** speed_setting. */
    public var speedSetting: DisplayMeasure?
        get() = (getFieldValue(SPEED_SETTING_FIELD_NUM) as? UByte)?.let { DisplayMeasure.fromValue(it) }
        set(value) = setFieldValue(SPEED_SETTING_FIELD_NUM, value?.value)

    /** dist_setting. */
    public var distSetting: DisplayMeasure?
        get() = (getFieldValue(DIST_SETTING_FIELD_NUM) as? UByte)?.let { DisplayMeasure.fromValue(it) }
        set(value) = setFieldValue(DIST_SETTING_FIELD_NUM, value?.value)

    /** power_setting. */
    public var powerSetting: DisplayPower?
        get() = (getFieldValue(POWER_SETTING_FIELD_NUM) as? UByte)?.let { DisplayPower.fromValue(it) }
        set(value) = setFieldValue(POWER_SETTING_FIELD_NUM, value?.value)

    /** activity_class. */
    public var activityClass: ActivityClass?
        get() = (getFieldValue(ACTIVITY_CLASS_FIELD_NUM) as? UByte)?.let { ActivityClass.fromValue(it) }
        set(value) = setFieldValue(ACTIVITY_CLASS_FIELD_NUM, value?.value)

    /** position_setting. */
    public var positionSetting: DisplayPosition?
        get() = (getFieldValue(POSITION_SETTING_FIELD_NUM) as? UByte)?.let { DisplayPosition.fromValue(it) }
        set(value) = setFieldValue(POSITION_SETTING_FIELD_NUM, value?.value)

    /** temperature_setting. */
    public var temperatureSetting: DisplayMeasure?
        get() = (getFieldValue(TEMPERATURE_SETTING_FIELD_NUM) as? UByte)?.let { DisplayMeasure.fromValue(it) }
        set(value) = setFieldValue(TEMPERATURE_SETTING_FIELD_NUM, value?.value)

    /** local_id. */
    public var localId: UShort?
        get() = getFieldValue(LOCAL_ID_FIELD_NUM) as? UShort
        set(value) = setFieldValue(LOCAL_ID_FIELD_NUM, value)

    /** Number of values stored in global_id. */
    public val numGlobalId: Int
        get() = getField(GLOBAL_ID_FIELD_NUM)?.numValues ?: 0

    /** global_id. */
    public var globalId: List<UByte?>?
        get() {
            val field = getField(GLOBAL_ID_FIELD_NUM) ?: return null
            return List(field.numValues) { getGlobalId(it) }
        }
        set(value) {
            removeField(GLOBAL_ID_FIELD_NUM)
            value?.forEachIndexed { index, element -> setGlobalId(index, element) }
        }

    public fun getGlobalId(index: Int): UByte? =
        getFieldValue(GLOBAL_ID_FIELD_NUM, index) as? UByte

    public fun setGlobalId(index: Int, value: UByte?) {
        setFieldValue(GLOBAL_ID_FIELD_NUM, value, index)
    }

    /** wake_time. Typical wake time */
    public var wakeTime: UInt?
        get() = getFieldValue(WAKE_TIME_FIELD_NUM) as? UInt
        set(value) = setFieldValue(WAKE_TIME_FIELD_NUM, value)

    /** sleep_time. Typical bed time */
    public var sleepTime: UInt?
        get() = getFieldValue(SLEEP_TIME_FIELD_NUM) as? UInt
        set(value) = setFieldValue(SLEEP_TIME_FIELD_NUM, value)

    /** height_setting. */
    public var heightSetting: DisplayMeasure?
        get() = (getFieldValue(HEIGHT_SETTING_FIELD_NUM) as? UByte)?.let { DisplayMeasure.fromValue(it) }
        set(value) = setFieldValue(HEIGHT_SETTING_FIELD_NUM, value?.value)

    /** user_running_step_length, in m. User defined running step length set to 0 for auto length */
    public var userRunningStepLength: Double?
        get() = getFieldValue(USER_RUNNING_STEP_LENGTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(USER_RUNNING_STEP_LENGTH_FIELD_NUM, value)

    /** user_walking_step_length, in m. User defined walking step length set to 0 for auto length */
    public var userWalkingStepLength: Double?
        get() = getFieldValue(USER_WALKING_STEP_LENGTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(USER_WALKING_STEP_LENGTH_FIELD_NUM, value)

    /** depth_setting. */
    public var depthSetting: DisplayMeasure?
        get() = (getFieldValue(DEPTH_SETTING_FIELD_NUM) as? UByte)?.let { DisplayMeasure.fromValue(it) }
        set(value) = setFieldValue(DEPTH_SETTING_FIELD_NUM, value?.value)

    /** dive_count. */
    public var diveCount: UInt?
        get() = getFieldValue(DIVE_COUNT_FIELD_NUM) as? UInt
        set(value) = setFieldValue(DIVE_COUNT_FIELD_NUM, value)

    public companion object {
        public const val MESSAGE_INDEX_FIELD_NUM: UByte = 254u
        public const val FRIENDLY_NAME_FIELD_NUM: UByte = 0u
        public const val GENDER_FIELD_NUM: UByte = 1u
        public const val AGE_FIELD_NUM: UByte = 2u
        public const val HEIGHT_FIELD_NUM: UByte = 3u
        public const val WEIGHT_FIELD_NUM: UByte = 4u
        public const val LANGUAGE_FIELD_NUM: UByte = 5u
        public const val ELEV_SETTING_FIELD_NUM: UByte = 6u
        public const val WEIGHT_SETTING_FIELD_NUM: UByte = 7u
        public const val RESTING_HEART_RATE_FIELD_NUM: UByte = 8u
        public const val DEFAULT_MAX_RUNNING_HEART_RATE_FIELD_NUM: UByte = 9u
        public const val DEFAULT_MAX_BIKING_HEART_RATE_FIELD_NUM: UByte = 10u
        public const val DEFAULT_MAX_HEART_RATE_FIELD_NUM: UByte = 11u
        public const val HR_SETTING_FIELD_NUM: UByte = 12u
        public const val SPEED_SETTING_FIELD_NUM: UByte = 13u
        public const val DIST_SETTING_FIELD_NUM: UByte = 14u
        public const val POWER_SETTING_FIELD_NUM: UByte = 16u
        public const val ACTIVITY_CLASS_FIELD_NUM: UByte = 17u
        public const val POSITION_SETTING_FIELD_NUM: UByte = 18u
        public const val TEMPERATURE_SETTING_FIELD_NUM: UByte = 21u
        public const val LOCAL_ID_FIELD_NUM: UByte = 22u
        public const val GLOBAL_ID_FIELD_NUM: UByte = 23u
        public const val WAKE_TIME_FIELD_NUM: UByte = 28u
        public const val SLEEP_TIME_FIELD_NUM: UByte = 29u
        public const val HEIGHT_SETTING_FIELD_NUM: UByte = 30u
        public const val USER_RUNNING_STEP_LENGTH_FIELD_NUM: UByte = 31u
        public const val USER_WALKING_STEP_LENGTH_FIELD_NUM: UByte = 32u
        public const val DEPTH_SETTING_FIELD_NUM: UByte = 47u
        public const val DIVE_COUNT_FIELD_NUM: UByte = 49u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[254] = Field("MessageIndex", 254u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[0] = Field("FriendlyName", 0u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[1] = Field("Gender", 1u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[2] = Field("Age", 2u, BaseType.UINT8, 1.0, 0.0, "years", false)
            fields[3] = Field("Height", 3u, BaseType.UINT8, 100.0, 0.0, "m", false)
            fields[4] = Field("Weight", 4u, BaseType.UINT16, 10.0, 0.0, "kg", false)
            fields[5] = Field("Language", 5u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[6] = Field("ElevSetting", 6u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[7] = Field("WeightSetting", 7u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[8] = Field("RestingHeartRate", 8u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[9] = Field("DefaultMaxRunningHeartRate", 9u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[10] = Field("DefaultMaxBikingHeartRate", 10u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[11] = Field("DefaultMaxHeartRate", 11u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[12] = Field("HrSetting", 12u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[13] = Field("SpeedSetting", 13u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[14] = Field("DistSetting", 14u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[16] = Field("PowerSetting", 16u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[17] = Field("ActivityClass", 17u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[18] = Field("PositionSetting", 18u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[21] = Field("TemperatureSetting", 21u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[22] = Field("LocalId", 22u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[23] = Field("GlobalId", 23u, BaseType.BYTE, 1.0, 0.0, "", false)
            fields[28] = Field("WakeTime", 28u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[29] = Field("SleepTime", 29u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[30] = Field("HeightSetting", 30u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[31] = Field("UserRunningStepLength", 31u, BaseType.UINT16, 1000.0, 0.0, "m", false)
            fields[32] = Field("UserWalkingStepLength", 32u, BaseType.UINT16, 1000.0, 0.0, "m", false)
            fields[47] = Field("DepthSetting", 47u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[49] = Field("DiveCount", 49u, BaseType.UINT32, 1.0, 0.0, "", false)
            return fields
        }
    }
}
